import React, { useState, useEffect } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import {
  FileText,
  Download,
  Calendar,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Filter,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Eye,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Smartphone
} from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { useCurrency } from '@/hooks/useCurrency'
import { parseDate, createDateRange, formatDateForInput } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import { ApiService } from '@/services/api'
import type { Bill, DateRange } from '@/types'
import { getBillStatusVariant, getBillStatusLabel, isCancelledBill, isRevenueBill } from '@/lib/billStatus'

// Only Swiggy bills created after the Net Sales change store `total` as the raw
// Net Sales figure (matches Swiggy's portal exactly) with `netSalesStored: true`,
// so the restaurant's commission % must be applied here to get the take-home amount.
// Older bills (and other platforms) already had the commission deducted before
// storage — using them as-is avoids double-deducting the commission.
const getReportAmount = (bill: any) => {
  const total = bill.total || 0
  const pct = bill.appProfitPercentage
  if (
    bill.onlinePlatform === 'Swiggy' &&
    bill.netSalesStored === true &&
    typeof pct === 'number' &&
    pct > 0
  ) {
    return Math.round(total * (1 - pct / 100) * 100) / 100
  }
  return total
}

export default function OnlineOrdersScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const { user } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const [bills, setBills] = useState<Bill[]>([])
  const [filteredBills, setFilteredBills] = useState<Bill[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [platformFilter, setPlatformFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [sortBy, setSortBy] = useState<'date' | 'billNumber' | 'platform' | 'total'>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const loadBills = async () => {
    if (!currentRestaurant?._id) return

    setIsLoading(true)
    setError(null)

    try {
      const response = await ApiService.getBills(currentRestaurant._id, dateRange, 'online')
      if (response.success) {
        setBills(response.data)
      } else {
        setError(response.message || 'Failed to load online orders')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load online orders')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (currentRestaurant?._id) {
      loadBills()
    }
  }, [currentRestaurant?._id])

  useEffect(() => {
    filterBills()
  }, [bills, searchTerm, platformFilter, statusFilter, sortBy, sortOrder])

  const filterBills = () => {
    let filtered = bills

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(bill =>
        bill.billNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.customerPhone?.includes(searchTerm) ||
        (bill as any).tableNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Filter by platform
    if (platformFilter !== 'all') {
      filtered = filtered.filter((bill: any) =>
        bill.orderType?.platform?.toLowerCase() === platformFilter.toLowerCase()
      )
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(bill => {
        if (statusFilter === 'completed') {
          return bill.status === 'completed' || bill.paymentStatus === 'completed' || bill.status === 'paid'
        }
        if (statusFilter === 'pending') {
          return bill.status === 'pending' || bill.paymentStatus === 'pending' || bill.paymentStatus === 'initiated'
        }
        if (statusFilter === 'cancelled') {
          return bill.status === 'cancelled' || bill.paymentStatus === 'cancelled'
        }
        return true
      })
    }

    // Sort bills
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'date':
          const dateA = new Date((a as any).billSubmittedDate || a.createdAt).getTime()
          const dateB = new Date((b as any).billSubmittedDate || b.createdAt).getTime()
          comparison = dateA - dateB
          break
        case 'billNumber':
          comparison = (a.billNumber || '').localeCompare(b.billNumber || '')
          break
        case 'platform':
          const platformA = (a as any).orderType?.platform || ''
          const platformB = (b as any).orderType?.platform || ''
          comparison = platformA.localeCompare(platformB)
          break
        case 'total':
          comparison = (a.total || 0) - (b.total || 0)
          break
      }

      return sortOrder === 'asc' ? comparison : -comparison
    })

    setFilteredBills(filtered)
  }

  const handleSort = (column: 'date' | 'billNumber' | 'platform' | 'total') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('desc')
    }
  }

  const handleViewBillItems = (bill: Bill) => {
    setSelectedBill(bill)
    setIsModalOpen(true)
  }

  const getSortIcon = (column: 'date' | 'billNumber' | 'platform' | 'total') => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-3 w-3 ml-1" />
    }
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />
  }

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    setDateRange(prev => {
      const newDate = parseDate(value)
      const updatedRange = field === 'from'
        ? createDateRange(newDate, prev.to)
        : createDateRange(prev.from, newDate)
      return updatedRange
    })
  }

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const getPaymentType = (bill: any) => {
    if (bill.paymentDetails && Array.isArray(bill.paymentDetails) && bill.paymentDetails.length > 0) {
      if (bill.paymentDetails.length > 1) {
        return bill.paymentDetails
          .map((payment: any) => {
            const type = payment.paymentType || payment.method || payment.type || 'Unknown'
            const amount = payment.amount || 0
            return `${type} (${formatCurrency(amount)})`
          })
          .join(', ')
      }
      const singlePayment = bill.paymentDetails[0]
      return singlePayment.paymentType || singlePayment.method || singlePayment.type || 'Cash'
    }
    return bill.paymentType || bill.paymentMethod || 'Cash'
  }

  const getPlatformBadgeColor = (platform: string) => {
    const platformLower = platform?.toLowerCase() || ''
    if (platformLower.includes('zomato')) return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    if (platformLower.includes('swiggy')) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    if (platformLower.includes('uber')) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
  }

  const exportToCSV = () => {
    const headers = ['Bill Number', 'Date & Time', 'Platform', 'Order ID', 'Items', 'Total', 'Payment Method', 'Status']
    const rows = filteredBills.map((bill: any) => [
      bill.billNumber || '',
      formatDateTime(bill.billSubmittedDate || bill.createdAt.toString()),
      bill.orderType?.displayName || bill.orderType?.platform || 'Online',
      bill.tableNumber || '',
      bill.items?.length || 0,
      bill.total || 0,
      getPaymentType(bill),
      isCancelledBill(bill) ? 'cancelled' : (bill.status || bill.paymentStatus || '')
    ])

    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `online-orders-${dateRange.from.toISOString().split('T')[0]}-to-${dateRange.to.toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  // Get unique platforms from bills
  const uniquePlatforms = Array.from(new Set(bills.map((bill: any) => bill.orderType?.platform).filter(Boolean)))

  // Calculate summary statistics (excluding active/unpaid and cancelled bills from revenue)
  const settledBills = filteredBills.filter(isRevenueBill)
  const totalRevenue = settledBills.reduce((sum, bill) => sum + getReportAmount(bill), 0)
  const totalOrders = settledBills.length
  const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0
  const completedOrders = settledBills.filter(bill =>
    bill.status === 'completed' || bill.paymentStatus === 'completed' || bill.status === 'paid'
  ).length

  // Platform-wise breakdown (settled bills only)
  const platformStats = uniquePlatforms.map(platform => {
    const platformOrders = settledBills.filter((bill: any) => bill.orderType?.platform === platform)
    const platformRevenue = platformOrders.reduce((sum, bill) => sum + getReportAmount(bill), 0)
    return {
      platform,
      orders: platformOrders.length,
      revenue: platformRevenue
    }
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading online orders...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button
              variant="outline"
              size="sm"
              className="ml-2"
              onClick={loadBills}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2 truncate">
            <Smartphone className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
            <span className="truncate">Online Orders</span>
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Platform orders
          </p>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <Button onClick={exportToCSV} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4">
            <Download className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
          <Button onClick={loadBills} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4">
            <RefreshCw className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Date Range and Filters - Desktop Only */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle className="text-base flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
            <div className="space-y-2 lg:col-span-2">
              <Label>Date Range</Label>
              <DateRangeModal
                dateRange={dateRange}
                onDateChange={handleDateChange}
                onApply={loadBills}
                title="Online Orders Date Range"
                description="Select the date range for online orders"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Bill, order ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="platform">Platform</Label>
              <Select value={platformFilter} onValueChange={setPlatformFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  {uniquePlatforms.map(platform => (
                    <SelectItem key={platform} value={platform?.toLowerCase() || ''}>
                      {platform}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Button onClick={loadBills} className="w-full sm:w-auto">
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(totalRevenue)}</div>
            <p className="text-xs text-muted-foreground">
              From {filteredBills.length} online orders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{totalOrders}</div>
            <p className="text-xs text-muted-foreground">
              {completedOrders} completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Order Value</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{formatCurrency(averageOrderValue)}</div>
            <p className="text-xs text-muted-foreground">
              Per order average
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Platforms</CardTitle>
            <Smartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold">{uniquePlatforms.length}</div>
            <p className="text-xs text-muted-foreground">
              Active platforms
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Platform Breakdown */}
      {platformStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Platform Breakdown</CardTitle>
            <CardDescription>Revenue and orders by platform</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {platformStats.map(stat => (
                <div key={stat.platform} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={getPlatformBadgeColor(stat.platform || '')}>
                      {stat.platform}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xl sm:text-2xl font-bold">{formatCurrency(stat.revenue)}</div>
                    <div className="text-sm text-muted-foreground">{stat.orders} orders</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Online Orders Data</span>
          </CardTitle>
          <CardDescription>
            Detailed view of all online orders
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <div className="max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-accent/50"
                      onClick={() => handleSort('billNumber')}
                    >
                      <div className="flex items-center">
                        Bill #
                        {getSortIcon('billNumber')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-accent/50"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center">
                        Date & Time
                        {getSortIcon('date')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-accent/50"
                      onClick={() => handleSort('platform')}
                    >
                      <div className="flex items-center">
                        Platform
                        {getSortIcon('platform')}
                      </div>
                    </TableHead>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead
                      className="cursor-pointer select-none hover:bg-accent/50"
                      onClick={() => handleSort('total')}
                    >
                      <div className="flex items-center">
                        Total
                        {getSortIcon('total')}
                      </div>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <Smartphone className="h-8 w-8 text-muted-foreground" />
                          <span className="text-muted-foreground">No online orders found</span>
                          <span className="text-sm text-muted-foreground">
                            Try adjusting your filters or date range
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBills.map((bill: any) => (
                      <TableRow key={bill._id}>
                        <TableCell className="font-medium">
                          {bill.billNumber || `#${bill._id.slice(-6)}`}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {formatDateTime(bill.billSubmittedDate || bill.createdAt.toString())}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getPlatformBadgeColor(bill.orderType?.platform)}>
                            {bill.orderType?.platform || 'Online'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-mono">
                            {bill.tableNumber || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {bill.items?.length || 0} items
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {getPaymentType(bill)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {formatCurrency(bill.total || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getBillStatusVariant(bill)}
                          >
                            {getBillStatusLabel(bill)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewBillItems(bill)}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {filteredBills.length === 0 ? (
              <div className="text-center py-8">
                <div className="flex flex-col items-center space-y-2">
                  <Smartphone className="h-8 w-8 text-muted-foreground" />
                  <span className="text-muted-foreground">No online orders found</span>
                  <span className="text-sm text-muted-foreground">
                    Try adjusting your filters or date range
                  </span>
                </div>
              </div>
            ) : (
              filteredBills.map((bill: any) => (
                <div key={bill._id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">
                      {bill.billNumber || `#${bill._id.slice(-6)}`}
                    </div>
                    <Badge
                      variant={getBillStatusVariant(bill)}
                    >
                      {getBillStatusLabel(bill)}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Platform:</span>
                      <Badge className={getPlatformBadgeColor(bill.orderType?.platform)}>
                        {bill.orderType?.platform || 'Online'}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Order ID:</span>
                      <span className="font-mono text-xs">{bill.tableNumber || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Items:</span>
                      <span>{bill.items?.length || 0} items</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Payment:</span>
                      <Badge variant="outline" className="capitalize text-xs">
                        {getPaymentType(bill)}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Date:</span>
                      <span className="text-xs">
                        {formatDateTime(bill.billSubmittedDate || bill.createdAt.toString())}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="font-semibold text-lg">
                      {formatCurrency(bill.total || 0)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewBillItems(bill)}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mobile Floating Filter */}
      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={loadBills}
        title="Online Orders Date Range"
        description="Select the date range for online orders"
      />

      {/* Bill Items Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Order Items - {selectedBill?.billNumber || 'N/A'}
            </DialogTitle>
            <DialogDescription>
              {(selectedBill as any)?.orderType?.displayName && `Platform: ${(selectedBill as any).orderType.displayName}`}
              {(selectedBill as any)?.billSubmittedDate && ` | ${formatDateTime((selectedBill as any).billSubmittedDate)}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Items Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-center">Quantity</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedBill?.items && selectedBill.items.length > 0 ? (
                    selectedBill.items.map((item, index) => (
                      <TableRow key={(item as any)._id || index}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.name}</div>
                            {(item as any).category && (
                              <div className="text-xs text-muted-foreground">
                                {(item as any).category}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {item.quantity}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.price)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.price * item.quantity)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                        No items found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Bill Summary */}
            {selectedBill && (
              <div className="space-y-2 border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span>{formatCurrency((selectedBill as any).subTotal || 0)}</span>
                </div>
                {(selectedBill as any).discountAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Discount ({(selectedBill as any).discountPercentage}%):
                    </span>
                    <span className="text-red-500">
                      -{formatCurrency((selectedBill as any).discountAmount)}
                    </span>
                  </div>
                )}
                {(selectedBill as any).serviceCharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Service Charge ({(selectedBill as any).scPercentage}%):
                    </span>
                    <span>{formatCurrency((selectedBill as any).serviceCharge)}</span>
                  </div>
                )}
                {(selectedBill as any).gst > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      GST ({(selectedBill as any).gstPercentage}%):
                    </span>
                    <span>{formatCurrency((selectedBill as any).gst)}</span>
                  </div>
                )}
                {(selectedBill as any).parcelCharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Parcel Charge:</span>
                    <span>{formatCurrency((selectedBill as any).parcelCharge)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-lg pt-2 border-t">
                  <span>Total:</span>
                  <span>{formatCurrency(selectedBill.total || 0)}</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
